import { http } from './http'
import { DEFAULT_SELLER_ENTRIES, SELLER_ENTRY_MODULES, type SellerEntryModule } from '@/navigation/sellerEntryScope'

export type SellerEntryMode = 'CLOSED' | 'GRAY' | 'OPEN'
export interface GraySellerAccount { tenantId: string; userId: string; tenantName: string | null; nickname: string | null }
export interface SellerEntryRule { module: SellerEntryModule; state: SellerEntryMode; grayAccounts: GraySellerAccount[] }
export interface SellerEntryConfig {
  version: number; source: 'BUILTIN_DEFAULT' | 'PLATFORM_DEFAULT'; modules: SellerEntryRule[]; maxStaleSeconds: number; canEdit: boolean
  lastOperationId: string | null; lastOperatorId: string | null
}
export interface ResolvedSellerEntries {
  version: number; source: SellerEntryConfig['source']; visibleModules: SellerEntryModule[]; maxStaleSeconds: number; expiresAt: string
}
export interface SellerEntryChange {
  expectedVersion: number; reason: string; clientOperationId: string;
  modules: Array<{ module: SellerEntryModule; state: SellerEntryMode; grayAccounts: Array<Pick<GraySellerAccount, 'tenantId' | 'userId'>> }>
}
export interface SellerEntryPreview { current: SellerEntryConfig; proposed: SellerEntryConfig; previewHash: string; maxStaleSeconds: number; scopeNote: string }
const BASE = '/ops/v1/seller-entry'
export const MAX_GRAY_SELLER_ACCOUNTS = 4
const validId = (value: unknown): value is string => typeof value === 'string' && /^[1-9][0-9]{0,18}$/.test(value) && BigInt(value) <= 9223372036854775807n
const isModule = (value: unknown): value is SellerEntryModule => SELLER_ENTRY_MODULES.includes(value as SellerEntryModule)
const bad = () => new Error('功能入口配置响应不完整，未采用该配置')
function metadata(value: unknown): asserts value is ResolvedSellerEntries {
  const row = value as ResolvedSellerEntries | null
  if (!row || !Number.isInteger(row.version) || row.version < 0 || row.version > 2147483647
    || !['BUILTIN_DEFAULT', 'PLATFORM_DEFAULT'].includes(row.source)
    || (row.source === 'BUILTIN_DEFAULT' && row.version !== 0)
    || !Number.isInteger(row.maxStaleSeconds) || row.maxStaleSeconds < 1 || row.maxStaleSeconds > 60) throw bad()
}
function account(value: unknown): value is GraySellerAccount {
  const row = value as GraySellerAccount | null
  return !!row && validId(row.tenantId) && validId(row.userId)
    && (row.tenantName == null || typeof row.tenantName === 'string') && (row.nickname == null || typeof row.nickname === 'string')
}
function config(value: unknown): SellerEntryConfig {
  metadata(value)
  const row = value as unknown as SellerEntryConfig
  if (!Array.isArray(row.modules) || row.modules.length !== SELLER_ENTRY_MODULES.length
    || new Set(row.modules.map(rule => rule?.module)).size !== SELLER_ENTRY_MODULES.length
    || row.modules.some(rule => !isModule(rule?.module) || !['CLOSED', 'GRAY', 'OPEN'].includes(rule.state)
      || !Array.isArray(rule.grayAccounts) || rule.grayAccounts.length > MAX_GRAY_SELLER_ACCOUNTS || rule.grayAccounts.some(item => !account(item))
      || new Set(rule.grayAccounts.map(item => `${item.tenantId}:${item.userId}`)).size !== rule.grayAccounts.length)) throw bad()
  if (new Set(row.modules.flatMap(rule => rule.grayAccounts.map(item => `${item.tenantId}:${item.userId}`))).size > MAX_GRAY_SELLER_ACCOUNTS) throw bad()
  if ((row.lastOperationId != null && (typeof row.lastOperationId !== 'string' || !/^[A-Za-z0-9._:-]{1,64}$/.test(row.lastOperationId)))
    || (row.lastOperatorId != null && !validId(row.lastOperatorId))) throw bad()
  return { ...row, canEdit: row.canEdit === true }
}
export async function getResolvedSellerEntries(): Promise<ResolvedSellerEntries> {
  const row = await http.get<ResolvedSellerEntries>('/api/v1/seller-entry')
  metadata(row)
  if (!Array.isArray(row.visibleModules) || row.visibleModules.some(value => !isModule(value))
    || new Set(row.visibleModules).size !== row.visibleModules.length
    || typeof row.expiresAt !== 'string' || !row.expiresAt.endsWith('Z') || !Number.isFinite(Date.parse(row.expiresAt))
    || Date.parse(row.expiresAt) <= Date.now() || Date.parse(row.expiresAt) > Date.now() + row.maxStaleSeconds * 1000 + 1000
    || (row.source === 'BUILTIN_DEFAULT' && (row.visibleModules.length !== DEFAULT_SELLER_ENTRIES.length
      || DEFAULT_SELLER_ENTRIES.some(module => !row.visibleModules.includes(module))))) throw bad()
  return row
}
export async function getSellerEntryConfig(): Promise<SellerEntryConfig> { return config(await http.get<unknown>(BASE)) }
export async function searchGraySellerAccounts(query: string): Promise<GraySellerAccount[]> {
  const text = query.trim()
  if (text.length < 2 || text.length > 100) return []
  const rows = await http.get<GraySellerAccount[]>(`${BASE}/accounts?query=${encodeURIComponent(text)}&limit=20`)
  if (!Array.isArray(rows) || rows.length > 20 || rows.some(value => !account(value))
    || new Set(rows.map(row => `${row.tenantId}:${row.userId}`)).size !== rows.length) throw bad()
  return rows
}
export async function previewSellerEntries(body: SellerEntryChange): Promise<SellerEntryPreview> {
  const value = await http.post<SellerEntryPreview>(`${BASE}/preview`, body)
  if (!value || typeof value.previewHash !== 'string' || !/^[a-f0-9]{64}$/.test(value.previewHash)
    || typeof value.scopeNote !== 'string' || value.maxStaleSeconds !== 60) throw bad()
  return { ...value, current: config(value.current), proposed: config(value.proposed) }
}
export async function saveSellerEntries(body: SellerEntryChange & { previewHash: string }): Promise<SellerEntryConfig> {
  return config(await http.put<unknown>(BASE, body))
}
